import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Observable, catchError, map, of } from 'rxjs';

import { CVA_CONSTANTS } from '../../util/constants';
import { environment } from '../../../environments/environment';
import { API_ROUTES } from '../../util/route.constants';
import { PatchIPreference, PreferencesResponse } from 'app/models/preferences.interface';
import { ClassificationEnum, DefaultPreferences, normalizeScheme } from 'app/util/user-config.constants';
import { FuseConfigService } from '@fuse/services/config';

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private cachedPreference: PreferencesResponse | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly fuseConfigService: FuseConfigService,
  ) { }

  getPreferences(
    referenceId: string,
    preferenceType: string,
    refresh: boolean = false
  ): Observable<PreferencesResponse> {
    // Return cached value if it exists and refresh is not requested
    if (!refresh && this.cachedPreference) {
      return of(this.cachedPreference);
    }

    const params = {
      referenceId,
      preferenceType
    };
    return this.http.get<{ response: PreferencesResponse[] }>(
      environment.cva_url + API_ROUTES.USER_SETTING, { params }
    ).pipe(
      map(response => {
        const item = response.response[0];

        const preference: PreferencesResponse =
          Object.keys(item.preference || {}).length === 0
            ? {
              preference: {
                ...DefaultPreferences,
              },
              systemTemplateAccessList: item.systemTemplateAccessList
            }
            : item
        // Cache the fetched preference
        if (!preference.preference.classificationType) {
          preference.preference.classificationType = ClassificationEnum.ICD;
        }
        preference.preference.scheme = normalizeScheme(preference.preference.scheme);
        this.setPreference(preference);
        return preference
      }),
      catchError((err: HttpErrorResponse) => this.handlePreferencesLoadError(err))
    )
  }

  private handlePreferencesLoadError(err: HttpErrorResponse): Observable<PreferencesResponse> {
    const forbidden =
      err?.status === 403 ||
      String(err?.error?.errorCode ?? '') === '403';

    if (forbidden) {
      return of(this.cachedPreference ?? { preference: { ...DefaultPreferences } });
    }

    const defaultPreference = { preference: { ...DefaultPreferences } };
    this.setPreference(defaultPreference);
    return of(defaultPreference);
  }

  patchPreference(
    preference: PatchIPreference,
    practitionerId: string,
    uid?: string
  ): Observable<PreferencesResponse> {
    const savedScheme = preference.preference?.scheme;

    if (!uid) {
      return this.savePreferences(
        preference,
        practitionerId,
      ).pipe(
        map(response => this.cacheSavedPreference(response.response, savedScheme)),
        catchError((err) => {
          console.error("Error saving preferences:", err);
          throw err;
        })
      )
    } else {
      return this.updatePreferences(
        uid,
        preference
      ).pipe(
        map(response => this.cacheSavedPreference(response.response, savedScheme)),
        catchError((err) => {
          console.error("Error updating preferences:", err);
          throw err;
        })
      )
    }
  }

  savePreferences(
    preference: PatchIPreference,
    practitionerId: string,
  ): Observable<{ response: PreferencesResponse }> {
    const payload = {
      preference: preference,
      preferenceType: CVA_CONSTANTS.CONST.USER_PREFERENCES,
      referenceId: practitionerId
    }
    return this.http.post<{ response: PreferencesResponse }>(
      environment.cva_url + API_ROUTES.USER_SETTING,
      payload
    );
  }

  updatePreferences(
    uid: string,
    preference: PatchIPreference
  ): Observable<{ response: PreferencesResponse }> {
    const params = { uid };
    return this.http.patch<{ response: PreferencesResponse }>(
      environment.cva_url + API_ROUTES.USER_SETTING,
      preference,
      { params })
  }

  setPreference(preference: PreferencesResponse, applyScheme = true): void {
    if (preference?.preference) {
      preference.preference.scheme = normalizeScheme(preference.preference.scheme);
      if (applyScheme) {
        this.applyScheme(preference.preference.scheme);
      }
    }
    this.cachedPreference = preference;
  }

  applyScheme(scheme?: string | null): void {
    this.fuseConfigService.config = { scheme: normalizeScheme(scheme) };
  }

  private cacheSavedPreference(
    preference: PreferencesResponse,
    savedScheme?: string
  ): PreferencesResponse {
    if (savedScheme !== undefined && preference?.preference) {
      preference.preference.scheme = normalizeScheme(
        preference.preference.scheme ?? savedScheme
      );
    }

    this.setPreference(preference, false);
    return preference;
  }

  removePreference(): void {
    this.cachedPreference = null;
  }

  clearPreferences(): void {
    this.removePreference();
    this.applyScheme('light');
  }
}
